import React, { useRef } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const getEduLabel = (value) => {
  switch (value) {
    case "E0":
      return "All Education Categories";
    case "E1":
      return "Less than high school";
    case "E2":
      return "High school or equivalent";
    case "E3":
      return "Some college / Associate degree";
    case "E4":
      return "Bachelor's or advanced degree";
    case "E5":
      return "Educational attainment not available";
    default:
      return "Unknown";
  }
};

export default function EducationTotals2020Chart({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) {
    return <p>No data available</p>;
  }

  /* ======================
     SUMMARY CALCULATIONS
  ======================= */

  const totalWorkforce = data.reduce(
    (sum, row) => sum + Number(row.total_2020 || 0),
    0,
  );

  const largestCategory = data.reduce(
    (max, row) =>
      Number(row.total_2020 || 0) > max.value
        ? {
            label: getEduLabel(row.education_label),
            value: Number(row.total_2020 || 0),
          }
        : max,
    {
      label: getEduLabel(data[0].education_label),
      value: Number(data[0].total_2020 || 0),
    },
  );

  /* ======================
     CHART DATA
  ======================= */

  const colors = [
    "#0ea5e9",
    "#ec4899",
    "#10b981",
    "#f59e0b",
    "#6366f1",
    "#ef4444",
  ];

  const chartData = {
    labels: data.map((d) => getEduLabel(d.education_label)),
    datasets: [
      {
        data: data.map((d) => Number(d.total_2020 || 0)),
        backgroundColor: colors,
        borderWidth: 2,
        hoverOffset: 10,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      title: {
        display: true,
        text: "📊 Education Distribution (2020)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = context.parsed;
            const percent = ((value / totalWorkforce) * 100).toFixed(1);
            return `${context.label}: ${value.toLocaleString()} (${percent}%)`;
          },
        },
      },
    },
  };

  /* ======================
     PDF FUNCTION
  ======================= */

  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdfDoc = new jsPDF("p", "mm", "a4");

      const pageWidth = pdfDoc.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdfDoc.addImage(utahData, "PNG", 60, 10, 32, 16);
      pdfDoc.setFontSize(26);
      pdfDoc.text("UTahData.org", 95, 22);
      pdfDoc.line(10, 35, 200, 35);

      pdfDoc.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);

      pdfDoc.line(10, 260, 200, 260);
      pdfDoc.addImage(utahData, "PNG", 22, 265, 30, 15);
      pdfDoc.setFontSize(10);
      pdfDoc.text("Built by", 55, 275);
      pdfDoc.addImage(bodex, "PNG", 68, 269, 44, 10);
      pdfDoc.text("using", 112, 275);
      pdfDoc.addImage(kpi, "PNG", 125, 265, 20, 20);
      pdfDoc.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        150,
        295,
      );
      pdfDoc.text(`From: ${window.location.href}`, 10, 295);

      pdfDoc.save("education_distribution_2020.pdf");
    });
  };

  /* ======================
     UI
  ======================= */

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Education Totals Distribution (2020)
        </h2>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Workforce (2020)
            </p>
            <p className="text-xl font-bold text-blue-800">
              {totalWorkforce.toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Largest Category
            </p>
            <p className="text-lg font-bold text-green-800">
              {largestCategory.label}
            </p>
          </div>

          <div className="p-4 bg-purple-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Largest Share</p>
            <p className="text-xl font-bold text-purple-800">
              {((largestCategory.value / totalWorkforce) * 100).toFixed(1)}%
            </p>
          </div>
        </div>

        {/* TABLE + CHART */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* TABLE */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Education</th>
                  <th className="p-2">Total (2020)</th>
                  <th className="p-2">Share %</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => {
                  const value = Number(row.total_2020 || 0);
                  const percent = ((value / totalWorkforce) * 100).toFixed(1);
                  return (
                    <tr key={index} className="hover:bg-gray-100">
                      <td className="p-2 font-medium">
                        {getEduLabel(row.education_label)}
                      </td>
                      <td className="p-2">{value.toLocaleString("en-US")}</td>
                      <td className="p-2 font-semibold text-blue-600">
                        {percent}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* CHART */}
          <div className="flex flex-col w-full bg-white rounded-lg shadow-md">
            <div className="w-full h-96 p-4">
              <Doughnut data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* DOWNLOAD BUTTON */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
